import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import {
  displayPair,
  followerCount,
  formatAmount,
  masterImage,
  statusClass,
  usdt,
} from "./helpers";
import MasterRating, { formatRating } from "./MasterRating";
import "./copyTrading.css";

function MasterDetail() {
  const { masterId } = useParams();
  const token = sessionStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();

  const [master, setMaster] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadDetail = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.copyTradingMasterDetail(masterId);
      if (result?.success) {
        const detail = result?.data || null;
        let followed = Boolean(detail?.isFollowing);
        if (!followed && token) {
          const followingRes = await AuthService.copyTradingMyFollowing();
          const list = Array.isArray(followingRes?.data) ? followingRes.data : [];
          followed = list.some((item) => String(item?.master_id) === String(masterId));
        }
        setIsFollowing(followed);
        setMaster(detail ? { ...detail, isFollowing: followed } : null);
      } else {
        setMaster(null);
        setIsFollowing(false);
        alertErrorMessage(result?.message || "Master not found");
      }
    } catch (error) {
      setMaster(null);
      setIsFollowing(false);
    } finally {
      setLoaded(true);
      LoaderHelper.loaderStatus(false);
    }
  };

  useEffect(() => {
    setLoaded(false);
    setIsFollowing(false);
    if (masterId) loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterId]);

  const requireLogin = () => {
    navigate("/login", { state: { redirectTo: location.pathname } });
  };

  const handleFollowToggle = async () => {
    if (!token) {
      requireLogin();
      return;
    }
    setBusy(true);
    try {
      const result = isFollowing
        ? await AuthService.copyTradingUnfollow(masterId)
        : await AuthService.copyTradingFollow(masterId);
      if (result?.success) {
        const nextFollowing = result?.data?.isFollowing ?? !isFollowing;
        setIsFollowing(Boolean(nextFollowing));
        setMaster((prev) => (prev ? { ...prev, isFollowing: Boolean(nextFollowing) } : prev));
        alertSuccessMessage(result.message);
      } else {
        alertErrorMessage(result?.message);
      }
    } catch (error) {
      alertErrorMessage("Unable to update follow status.");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) {
    return <section className="earning_outer_s ct-page" />;
  }

  if (!master) {
    return (
      <section className="earning_outer_s ct-page">
        <div className="container">
          <div className="earning_section_cate">
            <div className="no_data_outer">
              <div className="no_data_vector">
                <img src="/images/no_data_vector.svg" alt="no-data" />
              </div>
              <p>Master not found</p>
              <Link className="ct-back" to="/copy-trading">← Back to Copy Trading</Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const recentTrades = Array.isArray(master?.recentTrades) ? master.recentTrades : [];

  return (
    <>
      <Helmet>
        <title>{master?.name ? `${master.name} – Copy Trading` : "Master Profile – Copy Trading"}</title>
      </Helmet>

      <section className="earning_outer_s ct-page">
        <div className="container">
          <Link className="ct-back" to="/copy-trading">← Back to Copy Trading</Link>
          <div className="earning_section_cate">
          <div className="ct-detail-head">
            <div className={`ct-avatar-wrap lg ${isFollowing ? "is-following" : ""}`}>
              <img
                className="ct-avatar lg"
                src={masterImage(master?.profileImage)}
                alt={master?.name}
                onError={(e) => { e.currentTarget.src = "/images/user_icon.svg"; }}
              />
            </div>
            <div className="ct-detail-info">
              <span className="ct-kicker">Copy Trading Master</span>
              <div className="ct-name-row">
                <h2>{master?.name}</h2>
                <span className="ct-chip pair">{displayPair(master)}</span>
                {isFollowing && <span className="ct-chip following">Following</span>}
                {master?.displayWinRate != null && (
                  <span className="ct-win-badge">{formatAmount(master.displayWinRate, 0)}% Win Rate</span>
                )}
              </div>
              <MasterRating rating={master?.rating} size="lg" />
              <p>{master?.bio || "Professional copy trading master"}</p>
              <div className="ct-alloc">
                <div className="ct-alloc-item">
                  <label>Uses of your balance</label>
                  <strong>{master?.useBalancePercent || 0}%</strong>
                </div>
                {master?.profitPercent != null && (
                  <div className="ct-alloc-item gold">
                    <label>Up to profit</label>
                    <strong>{master.profitPercent}%</strong>
                  </div>
                )}
              </div>
              <div className="ct-notice">
                <span className="ct-notice-dot" />
                <p>Funds are taken from your spot USDT wallet. Pair shows {displayPair(master)}; PnL settles in USDT.</p>
              </div>
              <div className="ct-detail-actions">
                {token ? (
                  <button
                    type="button"
                    className={`ct-btn ${isFollowing ? "danger" : "primary"}`}
                    disabled={busy}
                    onClick={handleFollowToggle}
                  >
                    {isFollowing ? "Unfollow" : "Follow Master"}
                  </button>
                ) : (
                  <>
                    <Link
                      className="ct-btn primary"
                      to="/login"
                      state={{ redirectTo: location.pathname }}
                    >
                      Login to Follow
                    </Link>
                    <span className="ct-note" style={{ margin: 0 }}>
                      You can view this profile now. Login is required to follow.
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="ct-detail-stats">
            <div className="ct-detail-stat">
              <label>Rating</label>
              <span>{Number(master?.rating) > 0 ? formatRating(master?.rating) : "—"}</span>
            </div>
            <div className="ct-detail-stat">
              <label>Followers</label>
              <span>{formatAmount(followerCount(master), 0)}</span>
            </div>
            <div className="ct-detail-stat">
              <label>Funds Managed</label>
              <span>{usdt(master?.aum)}</span>
            </div>
            <div className="ct-detail-stat profit">
              <label>Total Profit Given</label>
              <span className="ct-pnl-positive">{usdt(master?.totalProfitGiven)}</span>
            </div>
            <div className="ct-detail-stat loss">
              <label>Total Loss</label>
              <span className="ct-pnl-negative">{usdt(master?.totalLossGiven)}</span>
            </div>
            <div className="ct-detail-stat win">
              <label>Win Rate</label>
              <span>{formatAmount(master?.displayWinRate, 0)}%</span>
            </div>
            <div className="ct-detail-stat">
              <label>Total Trades</label>
              <span>{formatAmount(master?.totalTrades, 0)}</span>
            </div>
          </div>
          </div>

          <div className="dashboard_recent_s ct-table-card">
            <h4 className="ct-section-title">Recent Trades</h4>
            <div className="table-responsive recenttable_s">
              <table>
                <thead>
                  <tr>
                    <th>Pair</th>
                    <th>Side</th>
                    <th>Price</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTrades.length > 0 ? recentTrades.map((trade) => (
                    <tr key={trade?._id}>
                      <td>{displayPair(trade)}</td>
                      <td className={`ct-side ${String(trade?.side || "").toLowerCase()}`}>{trade?.side || "—"}</td>
                      <td>{formatAmount(trade?.price)}</td>
                      <td>{formatAmount(trade?.amount)}</td>
                      <td>
                        <span className={`ct-status ${statusClass(trade?.status)}`}>
                          {trade?.status || "—"}
                        </span>
                      </td>
                      <td className={trade?.resultType === "PROFIT" ? "ct-pnl-positive" : trade?.resultType === "LOSS" ? "ct-pnl-negative" : ""}>
                        {trade?.resultType || "—"}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="6">
                        <div className="no_data_outer">
                          <div className="no_data_vector">
                            <img src="/images/no_data_vector.svg" alt="no-data" />
                          </div>
                          <p>No public trades yet</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default MasterDetail;
